import { FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { UserCircleIcon, AlertCircle, LogOut } from 'lucide-react';

import { useValidateRecoveryTokenQuery } from '../services/apiSlice';
import {
  restoreFromSession,
  resetSignUp,
} from '../features/signUp/signUpSlice';
import { store, useAppSelector } from '../stores/AppStore';
import { handleError } from '../services/ErrorService';
import { selectIsAuthenticated, logout } from '../features/user/userSlice';
import { apiSlice } from '../services/apiSlice';

import { Loading } from '../components/Loading';
import { Skeleton } from '../components/ui/skeleton';

const SignUpRecover: FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector((state) => state.user.user);
  const [emailMismatch, setEmailMismatch] = useState<
    | {
        sessionEmail: string;
        userEmail: string;
      }
    | undefined
  >(undefined);

  const {
    data: sessionData,
    isLoading,
    error,
  } = useValidateRecoveryTokenQuery(token ?? '', { skip: !token });

  useEffect(() => {
    if (!token || token.length !== 64) {
      toast.error('Invalid recovery link!');
      navigate('/sign-up');
      return;
    }

    if (error) {
      // Parse error from RTK Query response
      const errorData = (error as any)?.data;
      const errorCode = errorData?.errorCode;
      const errorMessage = errorData?.message || 'Recovery link is invalid';
      const userEmail = errorData?.email;

      // Determine user-friendly error message based on error code
      let displayMessage = '';

      switch (errorCode) {
        case 'TOKEN_EXPIRED': {
          displayMessage =
            'This recovery link has expired. Please check your email for our most recent message, or contact help@thinktankprep.com for assistance.';
          break;
        }
        case 'TOKEN_NOT_FOUND': {
          displayMessage =
            'This recovery link is invalid. Please check your email for our most recent message, or contact help@thinktankprep.com for assistance.';
          break;
        }
        default: {
          displayMessage =
            errorMessage ||
            'Unable to validate recovery link. Please contact help@thinktankprep.com for assistance.';
        }
      }

      // Track error in Sentry with context (only in production)
      handleError(
        new Error(
          `Sign-up recovery token validation failed: ${errorCode || 'UNKNOWN'}`,
        ),
        {
          displayedError: displayMessage,
          tags: {
            errorCode: errorCode || 'UNKNOWN',
            errorType: 'recovery_token_validation',
            tokenLength: token?.length || 0,
            tokenValid: token?.length === 64 ? 'true' : 'false',
            userEmail: userEmail || 'unknown',
          },
        },
      );

      navigate('/sign-up');
      return;
    }

    if (sessionData) {
      // Check if user is authenticated and email matches
      if (isAuthenticated && currentUser?.email) {
        const sessionEmail = sessionData.email.toLowerCase().trim();
        const userEmail = currentUser.email.toLowerCase().trim();

        if (sessionEmail !== userEmail) {
          // Email mismatch - show error with logout option
          setEmailMismatch({
            sessionEmail: sessionData.email,
            userEmail: currentUser.email,
          });
          return;
        }
      }

      // Email matches or user not authenticated - proceed with recovery
      // Restore the session state
      const formDataWithCorrectEmail = {
        ...sessionData.formData,
        mentor: {
          ...sessionData.formData.mentor,
          email: sessionData.email, // Use session email as the source of truth
        },
      };

      // Clear old sign-up state before restoring new session
      localStorage.removeItem('signUp-state');
      store.dispatch(resetSignUp(0));

      // Backend calculates the correct step based on actual database state
      // We trust this calculated step to ensure recovery always resumes at the right point
      store.dispatch(
        restoreFromSession({
          sessionId: sessionData.sessionId,
          currentStep: sessionData.currentStep, // Calculated by backend based on payment/mentor/thinker state
          formData: formDataWithCorrectEmail,
        }),
      );

      toast.success('Welcome back! Continuing where you left off...');
      navigate('/sign-up?recovered=true');
    }
  }, [token, sessionData, error, navigate, isAuthenticated, currentUser]);

  const handleLogoutAndContinue = () => {
    // Clear auth state
    localStorage.removeItem('signUp-state');
    store.dispatch(logout());
    store.dispatch(resetSignUp(0));
    store.dispatch(apiSlice.util.resetApiState());

    // Clear email mismatch error
    setEmailMismatch(undefined);

    // Toast will show automatically when recovery proceeds
  };

  // Show email mismatch error UI
  if (emailMismatch) {
    return (
      <div className="flex flex-col gap-4 md:gap-6 w-full md:w-[60%] lg:w-[50%] items-center mx-auto px-4 py-8 md:px-6 lg:px-8 bg-gray-100 rounded-xl">
        <div className="flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full">
          <AlertCircle className="w-8 h-8 text-yellow-600" />
        </div>

        <h1 className="text-2xl md:text-3xl font-medium text-center">
          Email Mismatch
        </h1>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 w-full">
          <p className="text-gray-700 mb-4">
            This recovery link is for a different email address:
          </p>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-600">
                Recovery link for:
              </span>
              <span className="text-blue-600 font-medium">
                {emailMismatch.sessionEmail}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-600">
                You're logged in as:
              </span>
              <span className="text-gray-900 font-medium">
                {emailMismatch.userEmail}
              </span>
            </div>
          </div>

          <p className="text-gray-600 text-sm">
            To continue with this recovery link, you need to log out of your
            current account first.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <button
            onClick={handleLogoutAndContinue}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Log Out & Continue
          </button>
          <button
            onClick={() => navigate('/login')}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>

        <p className="text-sm text-gray-500 text-center">
          Need help?{' '}
          <a
            href="mailto:help@thinktankprep.com"
            className="text-blue-600 hover:underline"
          >
            Contact support
          </a>
        </p>
      </div>
    );
  }

  return (
    <div
      className="
        flex flex-col gap-4 md:gap-6
        w-full md:w-[60%] lg:w-[50%]
        items-center
        mx-auto px-4 py-8 md:px-6 lg:px-8
        bg-gray-100 rounded-xl
      "
    >
      <h1 className="text-2xl md:text-3xl font-medium text-center">
        Continuing Your Sign-up
      </h1>
      <p className="text-gray-600 text-center">
        We're restoring your progress and will continue where you left off...
      </p>

      {isLoading ? (
        <>
          <Skeleton className="h-10 w-48 rounded-full bg-blue-100 mx-auto" />
          <div className="flex flex-col items-center gap-4">
            <Loading />
            <p className="text-sm text-gray-500">Validating recovery link...</p>
          </div>
        </>
      ) : sessionData ? (
        <>
          <div
            className="
              bg-blue-100 text-gray-600
              flex items-center gap-4
              px-3 py-2 rounded-full w-max mx-auto
              font-medium text-white bg-tttDefault
            "
          >
            <UserCircleIcon className="h-5 w-5 text-tttDefault" />
            {sessionData.email}
          </div>
          <div className="flex flex-col items-center gap-4">
            <Loading />
            <p className="text-sm text-gray-500">
              Restoring your sign-up progress...
            </p>
          </div>
        </>
      ) : undefined}
    </div>
  );
};

export default SignUpRecover;
